'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import {
    useAnalyticsPageTracker,
    useInitializeAnalytics,
    useInitializedAnalyticsEffect,
} from '@ntrinternet/ntr-analytics/react';
import { MeasureClickTypeName } from '@ntrinternet/ntr-analytics/vanilla';
import isProduction from '@/utils/isProduction';

const Analytics = () => {
    const pathname = usePathname();

    const { initialize, initializationState } = useInitializeAnalytics();
    const { setPageTrackerAndMeasurePageView } = useAnalyticsPageTracker();

    // Initialize Piano
    useEffect(() => {
        //
        if (
            !initializationState.isInitialized &&
            !initializationState.isInitializing
        ) {
            initialize({
                isDebug: false,
                brand: isProduction() ? 'Schooltv' : 'Schooltv - dev',
                brandId: isProduction() ? 630384 : 630385,
                platform: 'site',
                platformVersion: '1.0.0',
                environment: undefined,
                disableNmoDam: false,
                enabledPlugins: new Set(
                    isProduction() ? ['govolte'] : ['govolte', 'console'],
                ),
            });
        }
    }, [initializationState, initialize]);

    const lastSentPagePathname = useRef('');
    const updatePageTracker = useCallback(() => {
        if (!initializationState.isInitialized) return;

        if (lastSentPagePathname.current === pathname) return;
        lastSentPagePathname.current = pathname;

        const { name, chapter1, chapter2, chapter3 } =
            getAnalyticsPageNameAndChapters(pathname);

        // Create the page tracker
        setPageTrackerAndMeasurePageView(name, {
            chapters: {
                1: chapter1,
                2: chapter2,
                3: chapter3,
            },
        });
    }, [
        initializationState.isInitialized,
        pathname,
        setPageTrackerAndMeasurePageView,
    ]);

    // Run on every page change
    useEffect(updatePageTracker, [updatePageTracker]);
    // Also run when analytics have been initialized
    useInitializedAnalyticsEffect(updatePageTracker);

    return null;
};

const getAnalyticsPageNameAndChapters = (pathname: string) => {
    const pathWithoutFirstSlash = pathname.slice(1);
    const pathParts = pathWithoutFirstSlash.split('/');

    const isIndex = pathParts.length === 0;

    const [chapter2, chapter3, ...others] = pathWithoutFirstSlash.split('/');

    const name = isIndex
        ? 'index'
        : others.length
          ? others.join('/')
          : (chapter3 ?? chapter2 ?? '');

    return {
        pathParts,
        name,
        chapter1: 'schooltv-chat',
        chapter2:
            `${chapter2}`.length > 0 && chapter2 !== name
                ? chapter2
                : undefined,
        chapter3:
            `${chapter3}`.length > 0 && chapter2 !== name
                ? chapter3
                : undefined,
    };
};

export const measure = (
    name: string,
    type: MeasureClickTypeName = 'action',
) => {
    const { chapter1, chapter2, chapter3 } = getAnalyticsPageNameAndChapters(
        window.location.pathname,
    );

    window.ntrAnalytics?.measureClick(name, type, {
        chapters: {
            1: chapter1,
            2: chapter2,
            3: chapter3,
        },
    });
};

export default Analytics;
